import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsfEnum,
} from "@/utils/indicatorsEsf";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { ResumeIndicatorC3ByHealthTeamData } from "@/services/esus/indicator/esf/c3/getResumeIndicatorC3ByHealthTeam";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC3HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalPuerperals: Column;
	totalPuerperalsScore: Column;
	puerperalsClassificationPercentage: Column;
	puerperalsClassification: Column;
	totalPregnantsAndPuerperals: Column;
	totalPregnantsAndPuerperalsScore: Column;
	pregnantsAndPuerperalsClassificationPercentage: Column;
	pregnantsAndPuerperalsClassification: Column;
};

type IndicatorEsfC3HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorC3ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsfEnum.C3);

export function IndicatorEsfC3HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsfC3HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC3HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalPregnantsAndPuerperals: { title: "Gestantes e puérperas", visible: true },
		totalPregnantsAndPuerperalsScore: { title: "Pontuação", visible: true },
		pregnantsAndPuerperalsClassificationPercentage: { title: "% Classificação", visible: false },
		pregnantsAndPuerperalsClassification: { title: "Classificação", visible: false },
		totalPuerperals: { title: "Puérperas", visible: true },
		totalPuerperalsScore: { title: "Pontuação", visible: true },
		puerperalsClassificationPercentage: { title: "% Classificação", visible: true },
		puerperalsClassification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalPregnantsAndPuerperals.visible && "d-none"
						}`}
					>
						{columns.totalPregnantsAndPuerperals.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalPregnantsAndPuerperalsScore.visible && "d-none"
						}`}
					>
						{columns.totalPregnantsAndPuerperalsScore.title}
					</th>
					<th
						className={`px-2 ${
							!columns.pregnantsAndPuerperalsClassificationPercentage.visible &&
							"d-none"
						}`}
					>
						{columns.pregnantsAndPuerperalsClassificationPercentage.title}{" "}
						<CardOverlayTrigger
							header={"Classificação"}
							icon={<FaInfoCircle className="text-muted" />}
						>
							<div className="text-start">
								Percentual de pontuação das práticas realizadas em gestantes e puérperas<br />
								sobre a pontuação máxima alcançável (quantidade de gestantes e puérperas<br />
								X 100 (pontuação máxima por cidadão)).
							</div>
						</CardOverlayTrigger>
					</th>
					<th
						className={`px-2 ${
							!columns.pregnantsAndPuerperalsClassification.visible && "d-none"
						}`}
					>
						{columns.pregnantsAndPuerperalsClassification.title}{" "}
						<CardOverlayTrigger
							header={"Classificação"}
							icon={<FaInfoCircle className="text-muted" />}
						>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
								<br />
								<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.totalPuerperals.visible && "d-none"}`}>
						{columns.totalPuerperals.title}
					</th>
					<th className={`px-2 ${!columns.totalPuerperalsScore.visible && "d-none"}`}>
						{columns.totalPuerperalsScore.title}
					</th>
					<th
						className={`px-2 ${
							!columns.puerperalsClassificationPercentage.visible && "d-none"
						}`}
					>
						{columns.puerperalsClassificationPercentage.title}{" "}
						<CardOverlayTrigger
							header={"Classificação"}
							icon={<FaInfoCircle className="text-muted" />}
						>
							<div className="text-start">
								Percentual de pontuação das práticas realizadas em puérperas com finalização <br />
								gestacional no período sobre a pontuação máxima alcançável (quantidade <br />
								de puérperas X 100 (pontuação máxima por cidadão)).
							</div>
						</CardOverlayTrigger>
					</th>
					<th className={`px-2 ${!columns.puerperalsClassification.visible && "d-none"}`}>
						{columns.puerperalsClassification.title}{" "}
						<CardOverlayTrigger
							header={"Classificação"}
							icon={<FaInfoCircle className="text-muted" />}
						>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
								<br />
								<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC3HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC3HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC3HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalPregnantsAndPuerperals.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalPregnantsAndPuerperals ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalPregnantsAndPuerperalsScore.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalPregnantsAndPuerperalsScore ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.pregnantsAndPuerperalsClassificationPercentage
											.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalPregnantsAndPuerperalsScore,
										healthTeamClassification.maxPregnantsAndPuerperalsScore
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.pregnantsAndPuerperalsClassification.visible &&
										"d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C3,
											calculatePercentage(
												healthTeamClassification.totalPregnantsAndPuerperalsScore,
												healthTeamClassification.maxPregnantsAndPuerperalsScore
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C3,
											calculatePercentage(
												healthTeamClassification.totalPregnantsAndPuerperalsScore,
												healthTeamClassification.maxPregnantsAndPuerperalsScore
											)
										)}
									</Badge>
								</td>
								<td
									className={`px-2 ${
										!columns.totalPuerperals.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalPuerperals ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalPuerperalsScore.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalPuerperalsScore ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.puerperalsClassificationPercentage.visible &&
										"d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalPuerperalsScore,
										healthTeamClassification.maxPuerperalsScore
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.puerperalsClassification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C3,
											calculatePercentage(
												healthTeamClassification.totalPuerperalsScore,
												healthTeamClassification.maxPuerperalsScore
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C3,
											calculatePercentage(
												healthTeamClassification.totalPuerperalsScore,
												healthTeamClassification.maxPuerperalsScore
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
